import { parkUnityCanvas, releaseParkedUnityCanvas } from "@/lib/unity/canvas-park";
import {
  UNITY_TEARDOWN_HANG_TIMEOUT_MS,
  beginUnityTeardown,
  drainUnityTeardownEventLoop,
  endUnityTeardown,
} from "@/lib/unity/teardown-guard";

export { UNITY_TEARDOWN_HANG_TIMEOUT_MS };

type UnityQuitInstance = {
  Quit?: () => void | Promise<void>;
};

type TeardownSettleOptions = {
  onSettled?: () => void;
};

let quitBarrier: Promise<void> = Promise.resolve();
let hangTimeoutMs = UNITY_TEARDOWN_HANG_TIMEOUT_MS;
const hangTimers = new Set<ReturnType<typeof setTimeout>>();

function ignoreFailure(error: unknown): void {
  void error;
}

function settleOnce(resolve: () => void): () => void {
  let settled = false;
  return () => {
    if (settled) {
      return;
    }
    settled = true;
    resolve();
  };
}

/**
 * Queue `work` behind the current Quit. The returned promise (and the
 * module barrier) settle when work finishes *or* when its own hang timer
 * fires. That timer starts only when this work actually begins, so a slow
 * Quit ahead in the queue cannot eat a later Quit's timeout budget.
 */
function enqueueBarrierWork(work: () => Promise<unknown> | unknown): Promise<void> {
  let settleSlot!: () => void;
  const slot = new Promise<void>((resolve) => {
    settleSlot = settleOnce(resolve);
  });

  const previous = quitBarrier;
  quitBarrier = previous.then(
    () => slot,
    () => slot
  );

  void previous.then(
    async () => {
      const hangTimer = setTimeout(() => {
        hangTimers.delete(hangTimer);
        settleSlot();
      }, hangTimeoutMs);
      hangTimers.add(hangTimer);
      try {
        await work();
      } catch (error) {
        ignoreFailure(error);
      } finally {
        clearTimeout(hangTimer);
        hangTimers.delete(hangTimer);
        settleSlot();
      }
    },
    () => {
      settleSlot();
    }
  );

  return quitBarrier;
}

export function resetUnityQuitBarrierForTests(): void {
  quitBarrier = Promise.resolve();
  hangTimeoutMs = UNITY_TEARDOWN_HANG_TIMEOUT_MS;
  for (const timer of hangTimers) {
    clearTimeout(timer);
  }
  hangTimers.clear();
}

export function setUnityTeardownHangTimeoutForTests(ms: number): void {
  hangTimeoutMs = ms;
}

/** Resolves when every queued Quit() has settled. The next load awaits this. */
export function waitForUnityQuitBarrier(): Promise<void> {
  return quitBarrier;
}

/**
 * Append `quit` to the serialized Quit chain. The next call does not start
 * until this one settles, so two overlay closes cannot destroy WebGL in parallel.
 */
export function enqueueUnityQuit(quit: () => Promise<unknown> | unknown): Promise<void> {
  return enqueueBarrierWork(quit);
}

/**
 * After the current Quit chain, wait for `bootPromise` and Quit that instance.
 * Used when the player unmounts while `createUnityInstance` is still running.
 */
export function enqueueUnityQuitAfterBoot<TInstance extends UnityQuitInstance>(
  bootPromise: Promise<TInstance | null>,
  options?: { onBooted?: (instance: TInstance) => void }
): Promise<void> {
  return enqueueBarrierWork(() =>
    bootPromise.then(async (instance) => {
      if (!instance?.Quit) {
        return;
      }
      options?.onBooted?.(instance);
      await instance.Quit();
    })
  );
}

export async function completeParkedUnityTeardown(
  canvas: HTMLCanvasElement | null,
  onSettled?: () => void
): Promise<void> {
  await drainUnityTeardownEventLoop();
  if (canvas) {
    releaseParkedUnityCanvas(canvas);
  }
  endUnityTeardown();
  onSettled?.();
}

/** Park an owned canvas that has no Quit() API, then release after two frames. */
export function parkCanvasUntilEventLoopDrains(
  canvas: HTMLCanvasElement,
  options?: TeardownSettleOptions
): Promise<void> {
  beginUnityTeardown();
  parkUnityCanvas(canvas);
  return completeParkedUnityTeardown(canvas, options?.onSettled);
}

/**
 * Decide how a player unmount tears down its Unity session:
 * - booting: park now, Quit once createUnityInstance resolves
 * - running: park now, Quit on the serialized barrier
 * - owned but no Quit(): park until the event loop drains
 * - fetching only (Unity never touched the canvas): nothing to park
 */
export function startUnitySessionTeardown(options: {
  shouldAwaitBoot: boolean;
  instance: UnityQuitInstance | null;
  bootPromise: Promise<UnityQuitInstance | null> | null;
  canvas: HTMLCanvasElement | null;
  onSettled?: () => void;
}): void {
  const { shouldAwaitBoot, instance, bootPromise, canvas, onSettled } = options;

  if (shouldAwaitBoot && bootPromise) {
    bindTeardownToQuit(enqueueUnityQuitAfterBoot(bootPromise), canvas, { onSettled });
    return;
  }

  if (instance?.Quit) {
    const quit = instance.Quit.bind(instance);
    bindTeardownToQuit(
      enqueueUnityQuit(() => Promise.resolve(quit())),
      canvas,
      { onSettled }
    );
    return;
  }

  // Unity already owns the canvas but has no Quit() API. Park until the
  // current event loop drains so deleteContext is not racing React unmount.
  if (instance && canvas) {
    void parkCanvasUntilEventLoopDrains(canvas, { onSettled });
    return;
  }

  onSettled?.();
}

/**
 * Park the canvas and keep teardown active until `quitPromise` settles.
 * `quitPromise` comes from the barrier queue, which already abandons a
 * Quit that hangs past UNITY_TEARDOWN_HANG_TIMEOUT_MS once it has started.
 */
export function bindTeardownToQuit(
  quitPromise: Promise<void>,
  canvas: HTMLCanvasElement | null,
  options?: TeardownSettleOptions
): void {
  beginUnityTeardown();
  if (canvas) {
    parkUnityCanvas(canvas);
  }

  void quitPromise.finally(() => {
    void completeParkedUnityTeardown(canvas, options?.onSettled);
  });
}
